import { JoinOtpCertPageProps } from 'src/pages/JoinPage/JoinOtpCertPage/index'
import styled, { css } from 'styled-components'
import JoinTermsFlow from 'src/pages/JoinPage/_components/JoinTermsFlow'
import { useFormContext } from 'react-hook-form'
import { ReactHookFc, ReactHookForm } from 'src/components/common/ReactHookForm'
import { Noto_body1_b16, Noto_body3_r12 } from 'src/styles/fonts/NotoSansKrFonts'
import { Typography } from 'src/components/common/Typography'
import { Container } from 'src/components/common/Container'
import { JoinOtpCertFormItemsType } from 'src/types/react-hook-form'
import RenderIf from 'src/components/common/RenderIf'
import join_default from 'src/assets/images/join/join_default.png'
import join_default_fee from 'src/assets/images/join/join_default_fee.png'
import join_cert from 'src/assets/images/join/join_cert.png'
import join_cert_fee from 'src/assets/images/join/join_cert_fee.png'
import LazyImage from 'src/components/common/LazyImage'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import { useRef } from 'react'

const JoinOtpCertPageView = ({
  registerAttribute,
  onOtpCertVerifySubmit,
  onOtpCertRequestSubmit,
  onOtpCertRequestError,
  onOtpCertVerifyError,
  count,
  countStop,
  isOtpCertStep,
  isJoinTermsFlow,
  showMonthlyBenefitFee,
}: JoinOtpCertPageProps) => {
  const { c_transId } = useCustomCookies()
  const { formState, handleSubmit, watch } = useFormContext<JoinOtpCertFormItemsType>()
  const { errors, touchedFields } = formState

  const pNumberValue = watch('pNumber') ?? ''
  const certNumberValue = watch('certNumber') ?? ''

  const pNumberInputState = {
    length: pNumberValue.replace(/\D/g, '').length,
    isValid:
      touchedFields.pNumber !== undefined &&
      errors.pNumber === undefined &&
      pNumberValue.length === 11,
  }

  const certNumberInputState = {
    length: certNumberValue.length,
    isValid: certNumberValue.length === 4,
  }

  const isConfirmEnabled = !!c_transId && certNumberInputState.isValid

  const handleOtpRequestSubmit = handleSubmit(onOtpCertRequestSubmit, onOtpCertRequestError)
  const handleOtpCertSubmit = handleSubmit(onOtpCertVerifySubmit, onOtpCertVerifyError)

  // 인증번호input에서 joinTermsSubmit 할지 otpCertSubmit 할지 결정하는 함수(part1)
  const joinTermsSubmitRef = useRef<(() => void) | null>(null)

  const handleCertNumberInputSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isJoinTermsFlow) {
      joinTermsSubmitRef.current?.() // 가입 버튼과 같은 동작
      return
    }
    handleOtpCertSubmit(e) // 기존 회원이면 로그인
  }

  return (
    <S.JoinOtpCerContainer $option="flex_column_space_between" $width="calc(100% - 40px)">
      <Container $option="flex_column">
        <S.MonthlyBenefitCard>
          <RenderIf when={!isOtpCertStep}>
            <LazyImage
              src={showMonthlyBenefitFee ? join_default_fee : join_default}
              alt="휴대폰번호 입력 후 완료하세요"
              style={S.MonthlyBenefitCardImage}
            />
          </RenderIf>
          <RenderIf when={isOtpCertStep}>
            <LazyImage
              src={showMonthlyBenefitFee ? join_cert_fee : join_cert}
              alt="인증번호 입력 후 완료하세요"
              style={S.MonthlyBenefitCardImage}
            />
          </RenderIf>
        </S.MonthlyBenefitCard>

        <Container $option="flex_column" $row_gap="14px">
          <ReactHookForm style={S.OtpRequestWrap} onSubmit={handleOtpRequestSubmit}>
            <ReactHookFc.AgencyRadio {...registerAttribute.agency} />
            <S.PNumberInputWrap $option="flex_row" $column_gap="6px">
              <ReactHookFc.Input
                {...registerAttribute.pNumber}
                style={S.InputStyle(pNumberInputState.isValid)}
              />
              <S.RequestButton
                onClick={handleOtpRequestSubmit}
                disabled={!pNumberInputState.isValid}
              >
                {isOtpCertStep ? '재전송' : '전송'}
              </S.RequestButton>
            </S.PNumberInputWrap>
          </ReactHookForm>
          <RenderIf when={isOtpCertStep}>
            <ReactHookForm onSubmit={handleCertNumberInputSubmit} style={S.OtpSubmitWrap}>
              <Container>
                <ReactHookFc.Input
                  {...registerAttribute.certNumber}
                  style={S.InputStyle(certNumberInputState.isValid)}
                />
                <RenderIf when={isOtpCertStep}>
                  <S.CountDown $option="flex_row" $width="fit-content">
                    <Typography $fontStyle={Noto_body1_b16} $color="var(--black-50)">
                      {count?.substring(4, 12)}
                    </Typography>
                  </S.CountDown>
                </RenderIf>
              </Container>
            </ReactHookForm>
          </RenderIf>
        </Container>
        {isJoinTermsFlow ? (
          <JoinTermsFlow
            countStop={countStop}
            onJoinTermsSubmitReady={(fn) => {
              joinTermsSubmitRef.current = fn
            }}
          />
        ) : null}
      </Container>
      <RenderIf when={!isJoinTermsFlow}>
        <S.FooterContents $option="flex_column" $row_gap="10px">
          <S.SubmitButton type="button" onClick={handleOtpCertSubmit} disabled={!isConfirmEnabled}>
            확인
          </S.SubmitButton>
          <Typography $fontStyle={Noto_body3_r12} $color="var(--black-70)">
            미가입자의 경우 약관동의 화면으로 이동합니다.
          </Typography>
        </S.FooterContents>
      </RenderIf>
    </S.JoinOtpCerContainer>
  )
}
export default JoinOtpCertPageView

const S = {
  JoinOtpCerContainer: styled(Container)`
    max-width: 360px;
    flex: 1;
  `,
  MonthlyBenefitCard: styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    margin-bottom: 20px;
  `,
  MonthlyBenefitCardImage: css`
    min-height: 182px;
  `,
  PNumberInputWrap: styled(Container)`
    height: 48px;
  `,
  InputStyle: (isValid: boolean) => css`
    top: 0;
    right: 0;
    width: 100%;
    border-radius: 12px;
    background: var(--white);
    border: 1px solid var(--black-20);
    height: 48px;
    padding: 12px 16px;
    &::placeholder {
      color: var(--black-40);
    }
    &:disabled {
      border: 1px solid var(--black-10);
      background: var(--black-2);
      color: var(--black-40);
      cursor: not-allowed;
      -webkit-text-fill-color: var(--black-40);
    }
    ${isValid
      ? css`
          &:focus {
            border: 1px solid var(--brand-50);
          }
        `
      : css`
          border: 1px solid var(--red-50);
        `}
  `,
  OtpRequestWrap: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
  `,
  OtpSubmitWrap: css`
    width: 100%;
    display: flex;
    flex-direction: column;
  `,
  RequestButton: styled.button`
    display: block;
    width: 88px;
    min-width: 88px;
    height: 48px;
    text-align: center;
    ${Noto_body1_b16};
    border-radius: 12px;
    color: var(--brand-50);
    background-color: var(--brand-10);

    &:active {
      color: var(--brand-50);
      background-color: var(--brand-20);
    }

    &:disabled {
      background-color: var(--black-4);
      color: var(--black-20);
    }
  `,
  SubmitButton: styled.button`
    display: inline-block;
    width: 100%;
    margin: 0 auto;
    height: 48px;
    border-radius: 12px;
    text-align: center;
    background-color: var(--brand-50);
    ${Noto_body1_b16};
    color: var(--white);

    &:disabled {
      background-color: var(--black-4);
      color: var(--black-20);
      cursor: not-allowed;
    }
  `,
  CountDown: styled(Container)`
    align-items: center;
    position: absolute;
    height: 48px;
    float: right;
    top: 0;
    right: 16px;
  `,
  FooterContents: styled(Container)`
    text-align: center;
    padding: 20px 0 16px;
  `,
}
